import {LitElement, html, css} from 'lit';
import {customElement, state, property} from 'lit/decorators.js';
import {morphedRead, morphedCreate, morphedUpdate, morphedDelete} from '../../services/morphed-entity.service.js';
import {clamBackend, type EmailTemplateDraft} from '../../services/clam-backend.service.js';
import type {MediaFile} from '../../utils/media.js';
import {iconArrowLeft} from '../../utils/icons.js';
import '../clam-media-input/clam-media-input.js';

interface Evento {
	id: string;
	eventName: string;
}

interface Attachment extends MediaFile {
	label?: string;
}

interface EmailTemplate {
	id?: string;
	name?: string;
	isDefault?: boolean;
	evento?: string;
	subject?: string;
	includesQr?: boolean;
	includesPrivateData?: boolean;
	includesConfirmButton?: boolean;
	customIntro?: string;
	customInstructions?: string;
	attachments?: Attachment[] | null;
	primaryColor?: string;
	accentColor?: string;
}

@customElement('clam-email-template-editor')
export class ClamEmailTemplateEditor extends LitElement {
	static styles = css`
		:host { display: block; padding: var(--clam-space-6); max-width: 1200px; margin: 0 auto; font-family: var(--clam-font); color: var(--clam-text); }
		h2 { color: var(--clam-text); margin: 0 0 var(--clam-space-2); font-size: var(--clam-text-2xl); }
		p.desc { color: var(--clam-text-muted); font-size: var(--clam-text-base); margin: 0 0 var(--clam-space-6); }
		/* ===== GALLERY ===== */
		.toolbar { display: flex; justify-content: space-between; align-items: center; gap: var(--clam-space-3); margin-bottom: var(--clam-space-5); flex-wrap: wrap; }
		.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: var(--clam-space-4); }
		.card { background: var(--clam-surface); border: 1px solid var(--clam-border); border-radius: var(--clam-radius-md); overflow: hidden; cursor: pointer; transition: transform .15s, box-shadow .15s, border-color .15s; display: flex; flex-direction: column; position: relative; }
		.card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.08); border-color: var(--clam-primary); }
		.thumb { width: 100%; height: 180px; overflow: hidden; background: #f5f5f5; position: relative; pointer-events: none; }
		.thumb iframe { width: 600px; height: 760px; border: 0; transform: scale(0.46); transform-origin: top left; }
		.thumb-empty { display: flex; align-items: center; justify-content: center; height: 100%; color: var(--clam-text-muted); font-size: var(--clam-text-xs); }
		.card__body { padding: var(--clam-space-3) var(--clam-space-4); flex: 1; display: flex; flex-direction: column; gap: 4px; }
		.card__name { font-weight: 600; font-size: var(--clam-text-base); color: var(--clam-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.card__evento { font-size: var(--clam-text-xs); color: var(--clam-text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.card__del { position: absolute; top: 8px; right: 8px; background: rgba(0,0,0,0.5); color: #fff; border: none; border-radius: var(--clam-radius-sm); width: 32px; height: 32px; cursor: pointer; font-size: 16px; display: flex; align-items: center; justify-content: center; line-height: 1; }
		.card__del:hover { background: var(--clam-danger); }
		.badges { display: flex; gap: 6px; margin-top: 6px; flex-wrap: wrap; }
		.badge { font-size: 10px; padding: 2px 8px; border-radius: 20px; font-weight: 600; }
		.badge-on { background: rgba(90,157,30,0.15); color: var(--clam-green-dark); }
		.badge-off { background: var(--clam-surface-soft); color: var(--clam-text-muted); }
		.empty { padding: var(--clam-space-8); text-align: center; color: var(--clam-text-muted); border: 2px dashed var(--clam-border); border-radius: var(--clam-radius-md); }

		/* ===== EDITOR ===== */
		.back { display: inline-flex; align-items: center; gap: 6px; background: none; border: none; color: var(--clam-primary); font-family: inherit; font-size: var(--clam-text-sm); cursor: pointer; padding: 6px 0; margin-bottom: var(--clam-space-3); font-weight: 600; }
		.layout { display: grid; grid-template-columns: 1fr 1fr; gap: var(--clam-space-5); align-items: start; }
		@media (max-width: 900px) { .layout { grid-template-columns: 1fr; } }
		.panel { background: var(--clam-surface); border: 1px solid var(--clam-border); border-radius: var(--clam-radius-md); padding: var(--clam-space-5); }
		.panel h3 { margin: 0 0 var(--clam-space-4); font-size: var(--clam-text-lg); color: var(--clam-text); }
		.field { margin-bottom: var(--clam-space-4); }
		label { display: block; font-size: var(--clam-text-sm); font-weight: 600; color: var(--clam-text); margin-bottom: 6px; }
		input, select, textarea { width: 100%; padding: 10px 12px; border: 1px solid var(--clam-border); border-radius: var(--clam-radius-md); font-size: var(--clam-text-base); box-sizing: border-box; font-family: inherit; color: var(--clam-text); background: var(--clam-surface); }
		input:focus, select:focus, textarea:focus { outline: none; border-color: var(--clam-primary); box-shadow: 0 0 0 3px rgba(90,157,30,0.15); }
		textarea { min-height: 72px; resize: vertical; }
		.check { display: flex; align-items: center; gap: 8px; }
		.check input { width: auto; min-height: 40px; }
		.row3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: var(--clam-space-3); }
		@media (max-width: 640px) { .row3 { grid-template-columns: 1fr; } }
		@media (max-width: 520px) { .colorrow { flex-direction: column; gap: var(--clam-space-3); } }
		.btn { padding: 10px var(--clam-space-5); border: none; border-radius: var(--clam-radius-md); font-size: var(--clam-text-base); font-weight: 600; cursor: pointer; font-family: inherit; min-height: 44px; }
		.btn-primary { background: var(--clam-primary); color: var(--clam-on-primary); }
		.btn-primary:hover { background: var(--clam-primary-hover); }
		.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
		.btn-ghost { background: var(--clam-surface-soft); color: var(--clam-primary); }
		.colorrow { display: flex; gap: var(--clam-space-4); }
		.colorfield { flex: 1; }
		.colorfield .pick { display: flex; align-items: center; gap: 8px; }
		.colorfield input[type="color"] { width: 44px; height: 44px; padding: 2px; border: 1px solid var(--clam-border); border-radius: var(--clam-radius-md); background: var(--clam-surface); cursor: pointer; }
		.colorfield input[type="text"] { flex: 1; }
		.actions { display: flex; gap: var(--clam-space-3); margin-top: var(--clam-space-4); flex-wrap: wrap; }
		.msg { padding: var(--clam-space-3) var(--clam-space-4); border-radius: var(--clam-radius-md); font-size: var(--clam-text-sm); margin-top: var(--clam-space-3); }
		.msg-ok { background: #e8f8e8; color: var(--clam-green-dark); }
		.msg-err { background: #fde8e8; color: var(--clam-danger); }
		iframe.preview { width: 100%; min-height: 620px; border: 1px solid var(--clam-border); border-radius: var(--clam-radius-md); background: #fff; }
		.hint { font-size: var(--clam-text-xs); color: var(--clam-text-muted); margin-top: 4px; }
		.spinner { display: inline-block; width: 14px; height: 14px; border: 2px solid var(--clam-border); border-top-color: var(--clam-primary); border-radius: 50%; animation: spin 0.8s linear infinite; vertical-align: middle; }
		@keyframes spin { to { transform: rotate(360deg); } }
	`;

	@property() prefilledEvento?: string;

	@state() private _templates: EmailTemplate[] = [];
	@state() private _eventos: Evento[] = [];
	@state() private _previews = new Map<string, string>();
	@state() private _mode: 'gallery' | 'edit' = 'gallery';
	@state() private _draft: EmailTemplate = this._emptyDraft();
	@state() private _selectedId: string | null = null;
	@state() private _previewHtml = '';
	@state() private _previewSubject = '';
	@state() private _loading = true;
	@state() private _saving = false;
	@state() private _previewing = false;
	@state() private _msg: {text: string; kind: 'ok' | 'err'} | null = null;

	private _previewTimer: number | null = null;

	connectedCallback(): void {
		super.connectedCallback();
		void this._load();
	}

	private _emptyDraft(): EmailTemplate {
		return {name: '', evento: '', subject: '', includesQr: true, includesPrivateData: true, includesConfirmButton: true, customIntro: '', customInstructions: '', attachments: null, primaryColor: '', accentColor: ''};
	}

	private _eventoLabel(id?: string): string {
		return this._eventos.find((e) => e.id === id)?.eventName || '—';
	}

	private async _load(): Promise<void> {
		this._loading = true;
		try {
			const [tpls, evs] = await Promise.all([
				morphedRead<EmailTemplate>('Clam Email Template', {options: {limit: 100}}),
				morphedRead<Evento>('Clam Evento', {options: {limit: 100}}),
			]);
			this._templates = tpls;
			this._eventos = evs;
			await this._loadPreviews(tpls);
		} catch (e) {
			this._msg = {text: `Error cargando datos: ${e instanceof Error ? e.message : String(e)}`, kind: 'err'};
		} finally {
			this._loading = false;
			if (this.prefilledEvento) {
				this._openNew(this.prefilledEvento);
				this.prefilledEvento = undefined;
			}
		}
	}

	private async _loadPreviews(tpls: EmailTemplate[]): Promise<void> {
		const ctxFallback = this._eventos[0]?.id;
		const entries = await Promise.all(tpls.filter((t) => t.id).map(async (t) => {
			const ctx = (t.evento as string) || ctxFallback;
			if (!ctx) return [t.id as string, ''] as const;
			try {
				const r = await clamBackend.previewEmail(ctx, t.id);
				return [t.id as string, r.ok && r.data ? r.data.html : ''] as const;
			} catch {
				return [t.id as string, ''] as const;
			}
		}));
		this._previews = new Map(entries);
	}

	private _openNew(prefilledEvento?: string): void {
		this._selectedId = null;
		this._draft = {...this._emptyDraft(), evento: prefilledEvento || ''};
		this._previewHtml = '';
		this._previewSubject = '';
		this._msg = null;
		this._mode = 'edit';
		if (prefilledEvento) this._schedulePreview();
	}

	private async _delete(t: EmailTemplate): Promise<void> {
		if (!t.id) return;
		if (!window.confirm(`¿Eliminar la plantilla "${t.name || '(sin nombre)'}"? Esta acción no se puede deshacer.`)) return;
		try {
			await morphedDelete('Clam Email Template', [t.id]);
			this._msg = {text: 'Plantilla eliminada.', kind: 'ok'};
			await this._load();
		} catch (e) {
			this._msg = {text: `Error al eliminar: ${e instanceof Error ? e.message : String(e)}`, kind: 'err'};
			this.requestUpdate();
		}
	}

	private _openEdit(t: EmailTemplate): void {
		this._selectedId = t.id ?? null;
		this._draft = {...t, attachments: t.attachments ?? null};
		this._msg = null;
		this._mode = 'edit';
		this._schedulePreview();
	}

	private _backToGallery(): void {
		this._mode = 'gallery';
		this._msg = null;
	}

	private _setField(key: keyof EmailTemplate, value: unknown): void {
		this._draft = {...this._draft, [key]: value};
		this._schedulePreview();
	}

	private _draftAsTemplate(): EmailTemplateDraft {
		const d = this._draft;
		return {
			subject: d.subject?.trim() || undefined,
			customIntro: d.customIntro?.trim() || undefined,
			customInstructions: d.customInstructions?.trim() || undefined,
			includesQr: d.includesQr,
			includesPrivateData: d.includesPrivateData,
			includesConfirmButton: d.includesConfirmButton,
			primaryColor: d.primaryColor?.trim() || undefined,
			accentColor: d.accentColor?.trim() || undefined,
		};
	}

	private _previewContext(): string {
		return (this._draft.evento as string) || (this._eventos[0]?.id as string) || '';
	}

	private _schedulePreview(): void {
		if (!this._previewContext()) return;
		if (this._previewTimer) window.clearTimeout(this._previewTimer);
		this._previewTimer = window.setTimeout(() => void this._renderPreview(), 500);
	}

	@state() private _previewError = '';

	private async _renderPreview(): Promise<void> {
		const ctx = this._previewContext();
		if (!ctx) return;
		this._previewing = true;
		this._previewError = '';
		try {
			const r = await clamBackend.previewEmailTemplate(ctx, this._draftAsTemplate());
			if (r.ok && r.data) {
				this._previewHtml = r.data.html;
				this._previewSubject = r.data.subject;
			} else {
				// 401 = sesión expirada (lo más común). Sin esto el preview queda en blanco sin pistas.
				this._previewError = r.statusCode === 401
					? 'Tu sesión expiró. Recarga la página e inicia sesión de nuevo.'
					: (r.error || 'No se pudo generar la vista previa.');
			}
		} catch (e) {
			this._previewError = e instanceof Error ? e.message : 'Error de conexión al generar la vista previa.';
		} finally {
			this._previewing = false;
		}
	}

	private async _save(): Promise<void> {
		const d = this._draft;
		if (!d.name?.trim()) { this._msg = {text: 'El nombre es obligatorio.', kind: 'err'}; return; }
		if (!d.evento) { this._msg = {text: 'Selecciona un evento.', kind: 'err'}; return; }
		this._saving = true;
		this._msg = null;
		const atts = (d.attachments || []).map((a) => {
			const out: Record<string, unknown> = {};
			if (a.label) out.label = a.label;
			if (a._base64) out._base64 = a._base64;
			if (a.url) out.url = a.url;
			if (a.key) out.key = a.key;
			if (a.filename) out.filename = a.filename;
			if (a.size) out.size = a.size;
			if (a.mimeType) out.mimeType = a.mimeType;
			return out;
		});
		const payload: Record<string, unknown> = {
			name: d.name.trim(),
			isDefault: !!d.isDefault,
			evento: d.isDefault ? null : d.evento,
			subject: d.subject?.trim() || null,
			includesQr: !!d.includesQr,
			includesPrivateData: !!d.includesPrivateData,
			includesConfirmButton: !!d.includesConfirmButton,
			customIntro: d.customIntro?.trim() || null,
			customInstructions: d.customInstructions?.trim() || null,
			attachments: atts.length > 0 ? atts : null,
			primaryColor: d.primaryColor?.trim() || null,
			accentColor: d.accentColor?.trim() || null,
		};
		try {
			if (this._selectedId) {
				await morphedUpdate('Clam Email Template', this._selectedId, payload);
			} else {
				const ids = await morphedCreate('Clam Email Template', [payload]);
				this._selectedId = ids[0] ?? null;
			}
			this._msg = {text: 'Plantilla guardada.', kind: 'ok'};
			await this._load();
			this._mode = 'gallery';
		} catch (e) {
			this._msg = {text: `Error al guardar: ${e instanceof Error ? e.message : String(e)}`, kind: 'err'};
		} finally {
			this._saving = false;
		}
	}

	private _badge(on: boolean | undefined, label: string): unknown {
		return html`<span class="badge ${on ? 'badge-on' : 'badge-off'}">${label}</span>`;
	}

	render() {
		if (this._loading) return html`<div style="padding:40px;text-align:center"><span class="spinner"></span> Cargando...</div>`;
		return html`
			<h2>Plantillas de Correo</h2>
			<p class="desc">Diseñas aquí el correo de acceso/QR por evento: asunto, paleta, introducción, instrucciones, toggles y adjuntos. <strong>El envío se hace desde "Invitaciones y Correos"</strong>, donde eliges qué plantilla usar.</p>
			${this._mode === 'gallery' ? this._renderGallery() : this._renderEditor()}
			${this._msg && this._mode === 'gallery' ? html`<div class="msg ${this._msg.kind === 'ok' ? 'msg-ok' : 'msg-err'}">${this._msg.text}</div>` : ''}
		`;
	}

	private _renderGallery() {
		return html`
			<div class="toolbar">
				<span style="font-size:var(--clam-text-sm);color:var(--clam-text-muted)">${this._templates.length} plantilla(s)</span>
				<button class="btn btn-primary" @click=${() => this._openNew()}>+ Nueva plantilla</button>
			</div>
			${this._templates.length === 0
				? html`<div class="empty">Aún no hay plantillas.<br/><button class="btn btn-ghost" style="margin-top:12px" @click=${() => this._openNew()}>+ Crear la primera</button></div>`
				: html`<div class="grid">
					${this._templates.map((t) => {
						const previewHtmlStr = this._previews.get(t.id as string) || '';
						const atts = (t.attachments || []).length;
						return html`
							<div class="card" @click=${() => this._openEdit(t)}>
								${t.isDefault ? null : html`<button class="card__del" title="Eliminar" @click=${(e: Event) => { e.stopPropagation(); void this._delete(t); }}>&times;</button>`}
								<div class="thumb">
									${previewHtmlStr ? html`<iframe .srcdoc=${previewHtmlStr} sandbox></iframe>` : html`<div class="thumb-empty">sin vista previa</div>`}
								</div>
								<div class="card__body">
									<div class="card__name">${t.name || '(sin nombre)'}</div>
									<div class="card__evento">${t.isDefault ? 'Global — todos los eventos' : this._eventoLabel(t.evento)}</div>
									<div class="badges">
										${t.isDefault ? this._badge(true, 'Predeterminada') : null}
										${t.subject ? this._badge(true, 'Asunto') : this._badge(false, 'Asunto default')}
									${this._badge(t.includesQr !== false, 'QR')}
									${this._badge(t.includesPrivateData !== false, 'Privados')}
									${this._badge(t.includesConfirmButton !== false, 'Confirmación')}
									${atts > 0 ? this._badge(true, `${atts} adjunto${atts > 1 ? 's' : ''}`) : null}
									</div>
								</div>
							</div>
						`;
					})}
				</div>`}
		`;
	}

	private _renderEditor() {
		const d = this._draft;
		return html`
			<button class="back" @click=${() => this._backToGallery()}>${iconArrowLeft()} Volver a plantillas</button>
			<div class="layout">
				<div class="panel">
					<h3>${this._selectedId ? 'Editar plantilla' : 'Nueva plantilla'}</h3>
					<div class="field">
						<label>Nombre</label>
						<input .value=${d.name || ''} @input=${(e: Event) => this._setField('name', (e.target as HTMLInputElement).value)} placeholder="Ej: Acceso presencial estándar" />
					</div>
					<div class="field">
						<label>Evento</label>
						${d.isDefault
							? html`<input value="Global — aplica a todos los eventos" disabled />
								<div class="hint">Esta es la plantilla predeterminada: se usa cuando no se elige una específica al enviar.</div>`
							: html`<select .value=${d.evento || ''} @change=${(e: Event) => this._setField('evento', (e.target as HTMLSelectElement).value)}>
								<option value="">— Selecciona —</option>
								${this._eventos.map((e) => html`<option value=${e.id} ?selected=${d.evento === e.id}>${e.eventName}</option>`)}
							</select>`}
					</div>
				<div class="field">
					<label>Asunto</label>
					<input .value=${d.subject || ''} @input=${(e: Event) => this._setField('subject', (e.target as HTMLInputElement).value)} placeholder="Default: Tu acceso: {eventName}" />
					<div class="hint">Soporta el placeholder <code>{eventName}</code> (o <code>{evento}</code>) para el nombre del evento. Si lo dejas vacío, se usa el default.</div>
				</div>
					<div class="field">
						<label>Paleta de colores</label>
						<div class="colorrow">
							<div class="colorfield">
								<label style="font-weight:500;margin-bottom:4px">Primario (header/títulos)</label>
								<div class="pick">
									<input type="color" value=${d.primaryColor || '#1a3a5c'} @input=${(e: Event) => this._setField('primaryColor', (e.target as HTMLInputElement).value)} />
									<input type="text" .value=${d.primaryColor || ''} @input=${(e: Event) => this._setField('primaryColor', (e.target as HTMLInputElement).value)} placeholder="#1a3a5c" />
								</div>
							</div>
							<div class="colorfield">
								<label style="font-weight:500;margin-bottom:4px">Acento (botón)</label>
								<div class="pick">
									<input type="color" value=${d.accentColor || d.primaryColor || '#1a3a5c'} @input=${(e: Event) => this._setField('accentColor', (e.target as HTMLInputElement).value)} />
									<input type="text" .value=${d.accentColor || ''} @input=${(e: Event) => this._setField('accentColor', (e.target as HTMLInputElement).value)} placeholder="= primario" />
								</div>
							</div>
						</div>
					</div>
				<div class="row3">
					<div class="field">
						<label>QR</label>
						<div class="check"><input type="checkbox" ?checked=${d.includesQr} @change=${(e: Event) => this._setField('includesQr', (e.target as HTMLInputElement).checked)} /> <span>Incluir QR</span></div>
					</div>
					<div class="field">
						<label>Datos privados</label>
						<div class="check"><input type="checkbox" ?checked=${d.includesPrivateData} @change=${(e: Event) => this._setField('includesPrivateData', (e.target as HTMLInputElement).checked)} /> <span>Incluir info privada</span></div>
					</div>
					<div class="field">
						<label>Confirmación</label>
						<div class="check"><input type="checkbox" ?checked=${d.includesConfirmButton} @change=${(e: Event) => this._setField('includesConfirmButton', (e.target as HTMLInputElement).checked)} /> <span>Botón confirmar</span></div>
					</div>
				</div>
					<div class="field">
						<label>Introducción <span style="font-weight:400;color:var(--clam-text-muted)">(HTML permitido)</span></label>
						<textarea .value=${d.customIntro || ''} @input=${(e: Event) => this._setField('customIntro', (e.target as HTMLTextAreaElement).value)} placeholder="Default según modalidad"></textarea>
					</div>
					<div class="field">
						<label>Instrucciones <span style="font-weight:400;color:var(--clam-text-muted)">(HTML permitido)</span></label>
						<textarea .value=${d.customInstructions || ''} @input=${(e: Event) => this._setField('customInstructions', (e.target as HTMLTextAreaElement).value)} placeholder="Instrucciones adicionales para el asistente"></textarea>
					</div>
				<div class="field">
					<label>Documentos adjuntos</label>
					<clam-media-input accept="*/*" .multi=${true} .maxSize=${10 * 1024 * 1024} .value=${d.attachments ?? null} @change=${(e: CustomEvent) => this._setField('attachments', Array.isArray(e.detail) ? e.detail : e.detail ? [e.detail] : null)}></clam-media-input>
					<div class="hint">Se envían como <strong>archivos adjuntos reales</strong> en el correo (máx 10 MB por archivo).</div>
				</div>
					<div class="actions">
						<button class="btn btn-primary" ?disabled=${this._saving} @click=${() => this._save()}>${this._saving ? 'Guardando...' : 'Guardar plantilla'}</button>
						<button class="btn btn-ghost" @click=${() => this._backToGallery()}>Cancelar</button>
					</div>
					${this._msg ? html`<div class="msg ${this._msg.kind === 'ok' ? 'msg-ok' : 'msg-err'}">${this._msg.text}</div>` : ''}
				</div>

				<div class="panel">
					<h3>Vista previa ${this._previewing ? html`<span class="spinner"></span>` : ''}</h3>
					${this._previewSubject ? html`<div class="hint"><strong>Asunto:</strong> ${this._previewSubject}</div>` : ''}
				${this._previewError
					? html`<div class="msg msg-err">${this._previewError}</div>`
					: this._previewHtml
						? html`<iframe class="preview" .srcdoc=${this._previewHtml} sandbox></iframe>`
						: html`<div class="hint">Selecciona un evento para ver la vista previa del email.</div>`}
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'clam-email-template-editor': ClamEmailTemplateEditor;
	}
}
